'use client';
import Image from 'next/image';
import React, { RefObject, useCallback, useRef, useState } from 'react';

import { useIsTablet } from '@/app/_hooks/useIsScreen';
import Modal from '@/app/_components/modal';
import { MagnifyingGlassIcon, XMarkIcon, Bars3CenterLeftIcon } from '@heroicons/react/24/outline';
import { DAVINCI_CDN } from '@/costants/defaultValues';
import NavigationMenu from '@/app/_components/layouts/headerMenu/navigationMenu';
import ReactPopover from '@/app/_components/layouts/headerMenu/reactPopover';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { useOutsideClick } from '@/app/_hooks/useOutsideClick';
import Searchbar from '@/app/_components/searchbar';
import { HeaderLogo } from '@/app/_components/layouts/headerV2/headerLogo';
import { WebappCTAMenu } from '@/app/_components/layouts/headerMenu/webappCTAMenu';
import { usePatient } from '@/app/_hooks/usePatient';
import { PatientInfoButton } from '@/app/_components/layouts/headerMenu/patientInfoButton';

const logoFull = `${DAVINCI_CDN}/elty/elty-logo.svg`;

export const HeaderActions: React.FC<{ searchType: 'scroll' | 'popover'; defaultService: string | undefined }> = ({
  searchType,
  defaultService,
}) => {
  const [isSearchPopoverVisible, setIsSearchPopoverVisible] = useState(false);

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <button
        className="flex size-8 items-center justify-center p-1 lg:hidden"
        aria-label="Cerca"
        onClick={() => {
          if (searchType === 'scroll') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            setIsSearchPopoverVisible(true);
          }
        }}
      >
        <MagnifyingGlassIcon className="w-full text-primary" />
      </button>
      <div className="flex items-center">
        <div className="hidden items-center pr-2 lg:flex xl:pr-5">
          <WebappCTAMenu size="small" />
        </div>
        <HeaderMenu />
      </div>

      {isSearchPopoverVisible && (
        <HeaderSearchbarPopover
          defaultService={defaultService}
          onClose={() => {
            setIsSearchPopoverVisible(false);
          }}
        />
      )}
    </div>
  );
};

const HeaderSearchbarPopover: React.FC<{ onClose: () => void; defaultService: string | undefined }> = ({ onClose, defaultService }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  useOutsideClick(popoverRef as RefObject<HTMLDivElement>, () => onClose());

  return (
    <div
      className="absolute left-0 top-0 flex min-h-28 w-full flex-col items-center justify-center gap-6 bg-white p-5 shadow-header"
      ref={popoverRef}
    >
      <div className="grid w-full grid-cols-[24px_1fr_24px] items-center text-center">
        <HeaderLogo type="short" />
        <span className="text-lg font-medium text-primary">Ricerca</span>
        <button className="size-8 p-1" onClick={onClose}>
          <XMarkIcon className="cursor-pointer text-primary" />
        </button>
      </div>
      <Searchbar idPrefix="headerSearchBarPopover" openModalOnMobile defaultService={defaultService} className="w-full" />
    </div>
  );
};

const HeaderMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const togglePopover = useCallback(() => setIsOpen(open => !open), [setIsOpen]);

  const { patient } = usePatient();
  const isTabletAndSmaller = useIsTablet();
  const isWelfare = useIsWelfare();

  if (isWelfare) return;

  return (
    <>
      <ReactPopover isVisible={isOpen && !isTabletAndSmaller} setIsVisible={setIsOpen}>
        <div className="rounded-xl border border-neutral-100 bg-white pb-4 shadow-md">
          <NavigationMenu isMobile={isTabletAndSmaller} />
        </div>
      </ReactPopover>

      {patient && isTabletAndSmaller && <PatientInfoButton patient={patient} />}

      <button
        onClick={togglePopover}
        className={`relative flex size-9 items-center justify-center rounded-lg p-1 ${isOpen ? 'bg-neutral-active' : ''}`}
        aria-controls="navbar-menu"
        aria-expanded={isOpen}
        aria-label="Menu"
      >
        {isOpen ? <XMarkIcon className="w-full text-primary" /> : <Bars3CenterLeftIcon className="w-full text-primary" />}
      </button>

      <Modal
        isModalOpen={isOpen && isTabletAndSmaller}
        onClose={() => setIsOpen(false)}
        className="m-0 h-full max-h-full w-full max-w-full pb-14 pt-5"
      >
        <div className="relative mb-4 flex h-7 w-full items-center justify-between px-4">
          <Image src={logoFull} alt={'Elty'} height={56} width={722} className="left-0 h-full max-h-full w-auto" />
          <XMarkIcon className="size-7 cursor-pointer md:mr-5" aria-label="Chiudi" onClick={() => setIsOpen(false)} />
        </div>

        <div className="flex flex-col gap-y-5">
          <div>
            <NavigationMenu isMobile={isTabletAndSmaller} />
          </div>

          <hr className="h-px bg-current bg-neutral-surface" />

          <div className="w-full p-5 [&>button]:w-full">
            <WebappCTAMenu size="medium" isHiddenIfPatientExists={isTabletAndSmaller} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default HeaderMenu;
